import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { redeemApi } from "../../api";
import { Button } from "../../components/button/Button";
import { TextInput } from "../../components/text-input/TextInput";
import { setErrorAction, setInfoAction, setLoadingAction, setSuccessAction } from "../../store/actions/ui";
import { setShowLoginModalAction } from "../../store/actions/ui";
import { changeUserTokensByXAction } from "../../store/actions/user";
import { RootState } from "../../store/store";
import { ItemType } from "../../types";
import { formatPrice, itemToDefaultPrice, itemToDefaultTokens } from "../../utils/pricing";
import { StyledPricing } from "./pricing-style";
import { PageHeader } from "../../components/page-header/PageHeader";
import { SocialButton } from "../../components/social-button/SocialButton";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { COLORS } from "../../colors";
import { getLocalizedPath } from "../../utils/lang";
import { AlternateLinks } from '../../components/AlternateLinks';

export const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);
  const userFirebase = useSelector((state: RootState) => state.user.userFirebase);

  const [applyAffiliateDiscount, setApplyAffiliateDiscount] = useState<boolean>(false);

  const handleClickSubmitPromoCode = () => {
    if (userFirebase) {
      if (welcomeCodeTextInput) {
        if (import.meta.env.VITE_FLAGS_STOP_BUYING === "true") {
          dispatch(setInfoAction(t("pricing_maintenance_message")))
        } else {
          if (token) {
            dispatch(setLoadingAction(true));
            redeemApi(token, { promoCode: welcomeCodeTextInput }).then(res => {
              dispatch(setLoadingAction(false));
              if (res?.success) {
                if (res.data?.tokens) dispatch(changeUserTokensByXAction(res.data?.tokens)); //decrement tokens by 1
                dispatch(setSuccessAction(t("pricing_success_redeemed")));
              } else {
                if(res.customErrorCode){
                  switch (res.customErrorCode) {
                    case "MISSING_PARAMETERS":
                      dispatch(setErrorAction(t("pricing_error_missing_parameters")));
                      break;
                    case "PROMO_CODE_NOT_FOUND":
                      dispatch(setErrorAction(t("pricing_error_promo_not_found")));
                      break;
                    case "PROMO_CODE_ALREADY_REDEEMED":
                      dispatch(setErrorAction(t("pricing_error_promo_already_redeemed")));
                      break;
                    default:
                      break;
                  }
                } else {
                  dispatch(setErrorAction(t("pricing_error_unknown")));
                }
              }
            });
          }
        }
      }
    } else {
      dispatch(setShowLoginModalAction(true));
    }
  };

  const [welcomeCodeTextInput, setWelcomeCodeTextInput] = useState<string>("");
  const handleClickBuy = (item: ItemType) => {
    if (userFirebase) {
      if (userFirebase.emailVerified) {
        if (import.meta.env.VITE_FLAGS_STOP_BUYING === "true") {
          dispatch(setInfoAction(t("pricing_maintenance_message")))
        } else {
          navigate(getLocalizedPath('/checkout'), { state: { item } });
        }
      } else {
        dispatch(setInfoAction(t("pricing_email_not_verified")))
      }
    } else {
      dispatch(setShowLoginModalAction(true));
    }
  };

  const handleClickTry = () => {
    if (userFirebase) {
      navigate(getLocalizedPath('/'));
    } else {
      dispatch(setShowLoginModalAction(true));
    }
  };

  useEffect(() => {
    if (window.sessionStorage.getItem('affiliateKey')) {
      setApplyAffiliateDiscount(true);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('pricing_seo_title')}</title>
        <meta property="og:title" content={t('pricing_seo_title')} />
        <meta name="twitter:title" content={t('pricing_seo_title')} />
        <meta name="description" content={t('pricing_seo_description')} />
        <meta name="twitter:description" content={t('pricing_seo_description')} />
        <meta property="og:url" content={window.location.href} />
        {AlternateLinks().map((link, index) => (
          <link key={index} {...link} />
        ))}
      </Helmet>
      <StyledPricing>
        <PageHeader title="" subtitle={t("pricing_subtitle")} />

        <div className="bundles">

          <div className="bundle">
            <h1>{t("pricing_free_tokens_title")}</h1>
            <p><Trans i18nKey="pricing_free_tokens_desc" components={{ b: <b /> }} /></p>
            <p style={{ fontSize: '12px' }}>{t("pricing_free_tokens_reply")}</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
              <SocialButton social="x" />
            </div>
          </div>

          {!(import.meta.env.VITE_FLAGS_DISABLE_FREE_TRIAL === "true") && (
            <div className="bundle">
              <h1>{t("pricing_starter_bundle_title")}</h1>
              <p><Trans i18nKey="pricing_starter_bundle_desc" values={{ tokens: import.meta.env.VITE_FLAGS_FREE_TOKENS }} components={{ b: <b /> }} /></p>
              <Button disabled={import.meta.env.VITE_FLAGS_DISABLE_FREE_TRIAL === "true"} id="free" onClick={handleClickTry}>
                {t("pricing_try_for_free")}
              </Button>
            </div>
          )}

          <div className="bundle">
            <h1>{t("pricing_personal_bundle_title")}</h1>
            <p><Trans i18nKey="pricing_personal_bundle_desc" values={{ tokens: itemToDefaultTokens("personal") }} components={{ b: <b /> }} /></p>
            {applyAffiliateDiscount ? (
              <Button onClick={() => handleClickBuy("personal")}>
                <Trans i18nKey="pricing_buy_for_discount" values={{ 
                  originalPrice: formatPrice(itemToDefaultPrice("personal")), 
                  discountPrice: formatPrice(itemToDefaultPrice("personal", true))
                }} components={{ s: <s /> }} />
              </Button>
            ) : (
              <Button onClick={() => handleClickBuy("personal")}>
                {t("pricing_buy_for", { price: formatPrice(itemToDefaultPrice("personal")) })}
              </Button>
            )}

            {applyAffiliateDiscount && <p className="affiliateDiscountActive">{t("pricing_affiliate_discount_active")}</p>}

          </div>

          <div className="bundle">
            <h1>{t("pricing_growth_bundle_title")}</h1>
            <p><Trans i18nKey="pricing_growth_bundle_desc" values={{ tokens: itemToDefaultTokens("growth") }} components={{ b: <b /> }} /></p>

            {applyAffiliateDiscount ? (
              <Button onClick={() => handleClickBuy("growth")}>
                <Trans i18nKey="pricing_buy_for_discount" values={{ 
                  originalPrice: formatPrice(itemToDefaultPrice("growth")), 
                  discountPrice: formatPrice(itemToDefaultPrice("growth", true))
                }} components={{ s: <s /> }} />
              </Button>
            ) : (
              <Button onClick={() => handleClickBuy("growth")}>
                {t("pricing_buy_for", { price: formatPrice(itemToDefaultPrice("growth")) })}
              </Button>
            )}

            {applyAffiliateDiscount && <p className="affiliateDiscountActive">{t("pricing_affiliate_discount_active")}</p>}

          </div>

        </div>

        <div className="howitworks">
          <h1>{t("pricing_how_it_works_title")}</h1>
          <p><Trans i18nKey="pricing_how_it_works_desc" components={{ b: <b /> }} /></p>
        </div>

        <div className="Nosub">
          <h1>{t("pricing_no_sub_title")}</h1>
          <p><Trans i18nKey="pricing_no_sub_desc" components={{ b: <b /> }} /></p>
        </div>

        <div className="license">
          <h1>{t("pricing_license_title")}</h1>
          <p>
            <FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary} /> <Trans i18nKey="pricing_license_free" components={{ b: <b /> }} />
          </p>
          <p>
            <FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary} /> <Trans i18nKey="pricing_license_commercial" components={{ b: <b /> }} />
          </p>
          <p>
            <FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary} /> <Trans i18nKey="pricing_license_unique" components={{ b: <b /> }} />
          </p>
          <p>
            <FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary} /> <Trans i18nKey="pricing_license_no_permission" components={{ b: <b /> }} />
          </p>
        </div>

        <div className="promo">
          <h2>{t("pricing_redeem_title")}</h2>
          <p>{t("pricing_redeem_desc")}</p>
          <TextInput placeholder={t("pricing_welcome_code_placeholder")} value={welcomeCodeTextInput} onChange={(e: any) => setWelcomeCodeTextInput(e.target.value)} />
          <Button onClick={handleClickSubmitPromoCode}>{t("pricing_redeem_button")}</Button>
        </div>

      </StyledPricing>
    </>
  )
};